import React, { useCallback, FC, useMemo, useState } from "react";
import { View, useClientContext } from "@data-root/react";
import { core, coreConfig } from "..";
import { Empty, Layout } from "antd";
import { AppSpecifier, Manifest } from "@data-root/types";
import { LoaderFunctionArgs, redirect, useLoaderData, useNavigate } from "react-router";
import { Button as ShadButton } from "@/components/ui/button";
import { Card as ShadCard, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ThemeToggle } from "@/components/theme-toggle";
import { LanguageSwitcher } from "@/components/language-switcher";
import { Logo } from "@/components/logo";
import { useTranslation } from "react-i18next";
import { CornerDownRight, LogOut, RefreshCcw, Info } from "lucide-react";

interface AppTrunk {
  appId: string;
  branches: { appSpecifier: AppSpecifier; manifest: Manifest }[];
}

export interface LibraryRowProps {
  trunk: AppTrunk;
  onSelect: (branch: {
    appSpecifier: AppSpecifier;
    manifest: Manifest;
  }) => void;
}

interface LibraryRowsProps {
  trunks: AppTrunk[];
  onSelect: (app: { appSpecifier: AppSpecifier; manifest: Manifest }) => void;
}

const { Content, Header, Footer } = Layout;

// Responsive layout handled with Tailwind grid classes

export const LibraryRow: FC<LibraryRowProps> = ({ trunk, onSelect }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const onClick = useCallback(() => {
    onSelect(trunk.branches[selectedIndex]);
  }, [onSelect, selectedIndex, trunk.branches]);

  const onChangeBranch = useCallback(
    (branch: string) => {
      const index = trunk.branches.findIndex(
        (b) => b.appSpecifier.deploymentId === branch
      );
      setSelectedIndex(index);
    },
    [trunk.branches]
  );

  const branch = useMemo(
    () => trunk.branches[selectedIndex].appSpecifier.deploymentId,
    [selectedIndex, trunk.branches]
  );

  const name = useMemo(
    () => trunk.branches[selectedIndex].manifest.name,
    [selectedIndex, trunk.branches]
  );

  const description = useMemo(
    () => trunk.branches[selectedIndex].manifest.description,
    [selectedIndex, trunk.branches]
  );

  const options = useMemo(
    () =>
      trunk.branches.map((branch, i) => ({
        id: i,
        value: branch.appSpecifier.deploymentId,
        label: branch.appSpecifier.deploymentId,
      })),
    [trunk.branches]
  );

  // NEW: shadcn card replacing AntD card
  const { t } = useTranslation();
  
  // Truncate branch name to 3 chars with ellipsis
  const truncatedBranch = branch.length > 3 ? `${branch.slice(0, 3)}...` : branch;
  
  return (
    <ShadCard className="w-full h-full flex flex-col">
      <CardHeader className="flex-row items-start justify-between space-y-0 pb-2">
        <div className="min-w-0 flex-1 pr-2">
          <CardTitle className="truncate text-lg" title={name}>
            {name}
          </CardTitle>
        </div>
        {options.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ShadButton 
                variant="outline" 
                size="sm"
                className="h-7 px-2 text-xs shrink-0"
                title={branch}
              >
                {truncatedBranch}
              </ShadButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto min-w-[200px]">
              <DropdownMenuRadioGroup value={branch} onValueChange={onChangeBranch}>
                {options.map((opt) => (
                  <DropdownMenuRadioItem key={opt.value} value={opt.value} className="text-sm">
                    <span className="truncate" title={opt.label}>
                      {opt.label}
                    </span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-2">
        <div className="flex-1">
          {description ? (
            <div className="mb-2">
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {description}
              </p>
              {description.length > 120 && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <ShadButton 
                      variant="link" 
                      className="px-0 h-auto py-1 text-xs gap-1" 
                    >
                      <Info className="h-3 w-3" />
                      {t("view_details", { defaultValue: "View details" })}
                    </ShadButton>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{name}</DialogTitle>
                      <DialogDescription className="text-base mt-4 whitespace-pre-wrap">
                        {description}
                      </DialogDescription>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          ) : (
            <div className="mb-2 text-sm text-muted-foreground italic">
              {t("no_description", { defaultValue: "No description available" })}
            </div>
          )}
        </div>
        <div className="mt-auto">
          <ShadButton 
            variant="enterprise" 
            className="gap-1 w-full sm:w-auto" 
            size="sm" 
            onClick={onClick}
          >
            <CornerDownRight className="h-4 w-4" />
            {t("open")}
          </ShadButton>
        </div>
      </CardContent>
    </ShadCard>
  );
};

export const LibraryRows: FC<LibraryRowsProps> = ({ trunks, onSelect }) => {
  if (trunks.length === 0)
    return (
      <View style={{ height: "100%" }}>
        <Empty style={{ margin: "auto" }} />
      </View>
    );

  // NEW: Tailwind grid replacing AntD List grid - consistent spacing
  return (
    <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
      {trunks.map((item) => (
        <LibraryRow key={item.appId} trunk={item} onSelect={onSelect} />
      ))}
    </div>
  );
};

export const libraryScreenLoader = async (args: LoaderFunctionArgs) => {
  await core
    .recover(coreConfig)
    .catch((e) => {
      console.error(e);
      throw redirect("/auth");
    });
  if (!core.user) throw redirect("/auth");

  const apps = await core.getApps();

  const trunksMap: Record<
    string,
    { appSpecifier: AppSpecifier; manifest: Manifest }[]
  > = {};
  for (const appWithEnvironment of apps) {
    const { appSpecifier } = appWithEnvironment;
    if (!trunksMap[appSpecifier.appId]) trunksMap[appSpecifier.appId] = [];
    trunksMap[appSpecifier.appId].push(appWithEnvironment);
  }

  const trunks: AppTrunk[] = Object.entries(trunksMap)
    .map(([appId, apps]) => ({
      appId,
      branches: apps,
    }))
    .sort((a, b) => a.appId.localeCompare(b.appId));

  return trunks;
};

export const LibraryScreen: FC = () => {
  const trunks = useLoaderData() as Awaited<
    ReturnType<typeof libraryScreenLoader>
  >;

  const { refresh, logout } = useClientContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onClickRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  const onClickLogout = useCallback(async () => {
    await logout();
    navigate("/auth");
  }, [logout, navigate]);

  const onSelect = useCallback(
    async ({
      appSpecifier,
    }: {
      appSpecifier: AppSpecifier;
      manifest: Manifest;
    }) => {
      navigate(`/app/${appSpecifier.appId}/${appSpecifier.deploymentId}`);
    },
    [navigate]
  );

  // NEW: shadcn header with tooltips, theme and language toggles
  return (
    <Layout className="h-screen bg-background flex flex-col overflow-hidden">
      <Header className="!bg-background border-b dark:!bg-background sticky top-0 z-50 pt-[env(safe-area-inset-top)] shrink-0" style={{ height: "auto", background: "transparent", position: "sticky" }}>
        <div className="flex items-center gap-3 p-3 pb-3 sm:px-3 px-4">
          {/* Logo */}
          <Logo size={40} />
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <ShadButton 
                  variant="secondary" 
                  size="sm"
                  className="gap-1" 
                  onClick={onClickRefresh}
                >
                  <RefreshCcw className="h-4 w-4" /> 
                  <span className="hidden sm:inline">{t("refresh")}</span>
                </ShadButton>
              </TooltipTrigger>
              <TooltipContent>{t("refresh_help")}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <ShadButton 
                  variant="secondary" 
                  size="sm"
                  className="gap-1" 
                  onClick={onClickLogout}
                >
                  <LogOut className="h-4 w-4" /> 
                  <span className="hidden sm:inline">{t("signOut")}</span>
                </ShadButton>
              </TooltipTrigger>
              <TooltipContent>{t("signout_help")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </Header>

      <Content className="bg-background dark:bg-background flex-1 overflow-y-auto overflow-x-hidden" style={{ background: "transparent" }}>
        <LibraryRows trunks={trunks} onSelect={onSelect} />
      </Content>

      <Footer className="bg-background dark:bg-background border-t border-border shrink-0" style={{ background: "transparent" }}>
        <div className="text-sm text-foreground px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
          {t("version")}: <code className="select-all text-muted-foreground">{__GIT_HASH__}</code>
        </div>
      </Footer>
    </Layout>
  );
};
